import React, { createContext, useContext, useEffect, useState } from 'react';
import PWAManager from '../services/pwa';

interface PWAContextType {
  isSupported: boolean;
  isInstalled: boolean;
  isInstallable: boolean;
  isOffline: boolean;
  hasUpdate: boolean;
  isLoading: boolean;
  installApp: () => Promise<boolean>;
  updateApp: () => Promise<void>;
  clearCache: () => Promise<void>;
  cacheImportantData: () => Promise<void>;
}

const PWAContext = createContext<PWAContextType | undefined>(undefined);

interface PWAProviderProps {
  children: React.ReactNode;
}

export const PWAProvider: React.FC<PWAProviderProps> = ({ children }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializePWA = async () => {
      // Check if PWA feature is enabled
      const isPWAEnabled = import.meta.env.VITE_ENABLE_PWA === 'true';

      if (!isPWAEnabled) {
        setIsLoading(false);
        setIsSupported(false);
        return;
      }

      setIsLoading(true);

      try {
        // Register service worker
        await PWAManager.registerServiceWorker();
        
        // Set initial state
        const status = PWAManager.getStatus();
        setIsSupported(status.isSupported);
        setIsInstalled(status.isInstalled);
        setIsInstallable(status.installPrompt !== null);
        setIsOffline(status.isOffline);
        
        // Cache important data for offline use
        await PWAManager.cacheImportantData();
        
      } catch (error) {
        console.error('PWA initialization failed:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializePWA();
  }, []);

  useEffect(() => {
    // Set up event listeners
    const handleInstallPrompt = () => {
      setIsInstallable(true);
    };

    const handleInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
    };

    const handleOffline = () => {
      setIsOffline(true);
    };

    const handleOnline = () => {
      setIsOffline(false);
    };

    const handleUpdate = () => {
      setHasUpdate(true);
    };

    PWAManager.onInstallPrompt(handleInstallPrompt);
    PWAManager.onInstalled(handleInstalled);
    PWAManager.onOffline(handleOffline);
    PWAManager.onOnline(handleOnline);
    PWAManager.onUpdate(handleUpdate);

    // Clean up is not needed as PWAManager is a singleton
    return () => {
      // Event listeners are managed by PWAManager
    };
  }, []);

  const installApp = async (): Promise<boolean> => {
    try {
      const result = await PWAManager.promptInstall();
      if (result) {
        setIsInstalled(true);
        setIsInstallable(false);
      }
      return result;
    } catch (error) {
      console.error('App installation failed:', error);
      return false;
    }
  };

  const updateApp = async (): Promise<void> => {
    try {
      await PWAManager.updateServiceWorker();
      setHasUpdate(false);
    } catch (error) {
      console.error('App update failed:', error);
    }
  };

  const clearCache = async (): Promise<void> => {
    try {
      await PWAManager.clearCache();
    } catch (error) {
      console.error('Cache clearing failed:', error);
    }
  };

  const cacheImportantData = async (): Promise<void> => {
    try {
      await PWAManager.cacheImportantData();
    } catch (error) {
      console.error('Data caching failed:', error);
    }
  };

  const value: PWAContextType = {
    isSupported,
    isInstalled,
    isInstallable,
    isOffline,
    hasUpdate,
    isLoading,
    installApp,
    updateApp,
    clearCache,
    cacheImportantData,
  };

  return (
    <PWAContext.Provider value={value}>
      {children}
    </PWAContext.Provider>
  );
};

export const usePWA = (): PWAContextType => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};
